import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  List,
  Tag,
  Avatar,
  Button,
  Modal,
  Form,
  Select,
  Input,
  Spin,
  message,
} from 'antd';
import {
  LineChartOutlined,
  CoffeeOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { timeTrackerService, workerListService } from '@iso/services';
import { WORK_TYPES, TIME_ENTRY_STATUS, formatDuration } from '@iso/constants/timeTracker';
import { StatCard, SectionCard, EmptyBox, OPS_COLORS, initials } from './OperationsUI';

const { Option } = Select;

const recentIcon = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('break')) return { icon: <CoffeeOutlined />, color: OPS_COLORS.orange };
  if (l.includes('clocked in')) return { icon: <PlayCircleOutlined />, color: OPS_COLORS.greenBright };
  return { icon: <ClockCircleOutlined />, color: OPS_COLORS.grey };
};

const TimeTracker = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const currentUser = useSelector((state) => state.user.user);
  const activePermission = useSelector((state) => state.permission.activePermission);
  const activeProperty = commonHelper.getActiveProperty();
  // Owners/admins (managers) may clock in / out / break any worker on the
  // property; everyone else is limited to their own time entry.
  const canManageOthers = ['owner', 'admin'].includes(activePermission);

  const [dashboard, setDashboard] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clockInOpen, setClockInOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());

  const [form] = Form.useForm();
  // Wall-clock time (ms) the dashboard figures were fetched at, so the live
  // timers can extend each worker's server-computed worked_seconds locally
  // without re-hitting the API every second.
  const fetchedAtRef = useRef(Date.now());
  // Guard against setState after unmount (this view auto-refreshes on an
  // interval, so a response can land after the user has navigated away).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadDashboard = () => {
    if (!activePropertyId) return;
    setLoading(true);
    timeTrackerService
      .getDashboard(activePropertyId)
      .then((res) => {
        if (!mountedRef.current) return;
        setDashboard(res?.data || null);
        fetchedAtRef.current = Date.now();
      })
      // Silent: this is a once-a-minute background refresh, so don't spam a
      // toast on a transient failure — just avoid an unhandled rejection.
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePropertyId]);

  // Managers pick who to clock in from the property roster; only linked app
  // users (those with a user_id) can own a time entry, so contractors without
  // a login are excluded.
  useEffect(() => {
    if (!activePropertyId || !canManageOthers) return;
    workerListService
      .getList(activePropertyId)
      .then((res) => setRoster((res?.workers || []).filter((w) => w.is_active && w.user_id)))
      .catch(() => {});
  }, [activePropertyId, canManageOthers]);

  // Tick every second to advance the live clock and worker timers; refresh the
  // dashboard from the server once a minute to pick up others' changes.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const refresh = setInterval(loadDashboard, 60000);
    return () => {
      clearInterval(tick);
      clearInterval(refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePropertyId]);

  const liveSeconds = (worker) => {
    const base = worker.worked_seconds || 0;
    if (worker.status === 'active') {
      return base + Math.floor((now - fetchedAtRef.current) / 1000);
    }
    return base;
  };

  const runAction = (promise, successMsg) => {
    promise
      .then((res) => {
        if (res && res.code === '0000') {
          if (successMsg) message.success(successMsg);
          loadDashboard();
        } else {
          message.error(res?.message || 'Action failed');
        }
      })
      .catch(() => message.error('Action failed'));
  };

  // Clock-in target options for managers: the roster's linked users, ensuring
  // the current user is always selectable even if not yet on the roster.
  const workerOptions = (() => {
    const list = roster.map((w) => ({ user_id: w.user_id, name: w.name }));
    if (currentUser?.id && !list.some((w) => w.user_id === currentUser.id)) {
      list.unshift({ user_id: currentUser.id, name: currentUser.name });
    }
    return list;
  })();

  const handleClockIn = (values) => {
    setSubmitting(true);
    // Non-managers can only ever clock themselves in (the backend enforces this
    // too); managers use the selected worker, defaulting to themselves.
    const targetUserId = canManageOthers ? values.worker || currentUser?.id : currentUser?.id;
    const isOther = targetUserId && targetUserId !== currentUser?.id;
    const targetName = isOther
      ? workerOptions.find((w) => w.user_id === targetUserId)?.name || 'Worker'
      : null;
    timeTrackerService
      .clockIn({
        property_id: activePropertyId,
        user_id: targetUserId,
        work_type: values.work_type || null,
        location: values.location || activeProperty?.name || null,
      })
      .then((res) => {
        if (res && res.code === '0000') {
          message.success(targetName ? `${targetName} clocked in` : 'Clocked in');
          setClockInOpen(false);
          form.resetFields();
          loadDashboard();
        } else {
          message.error(res?.message || 'Could not clock in');
        }
      })
      .catch(() => message.error('Could not clock in'))
      .finally(() => setSubmitting(false));
  };

  const activeWorkers = dashboard?.active_workers || [];
  const recent = dashboard?.recent_activity || [];

  // Group the clocked-in workers by location for the "By Property" summary.
  const byLocation = Object.values(
    activeWorkers.reduce((acc, w) => {
      const key = w.location || 'Unassigned';
      acc[key] = acc[key] || { location: key, count: 0 };
      acc[key].count += 1;
      return acc;
    }, {})
  );

  return (
    <LayoutWrapper style={{ display: 'block' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontWeight: 700, fontSize: 26 }}>Time Tracker</h1>
          <div style={{ color: OPS_COLORS.grey }}>{moment(now).format('dddd D MMMM YYYY')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: OPS_COLORS.grey, fontSize: 13 }}>Current Time</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{moment(now).format('hh:mm:ss a')}</div>
          </div>
          <Button type="primary" onClick={() => setClockInOpen(true)}>
            Clock In
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Currently Clocked In"
              value={
                <span style={{ color: OPS_COLORS.greenBright }}>
                  {dashboard?.clocked_in_count || 0}
                </span>
              }
              icon={<LineChartOutlined />}
              accent={OPS_COLORS.green}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="On Break"
              value={<span style={{ color: OPS_COLORS.orange }}>{dashboard?.on_break_count || 0}</span>}
              icon={<CoffeeOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Hours Today"
              value={dashboard?.hours_today || 0}
              icon={<ClockCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Active Locations"
              value={dashboard?.active_locations || 0}
              icon={<EnvironmentOutlined />}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 16 }}>
          <Col xs={24} lg={14}>
            <SectionCard title="Active Workers" fill style={{ minHeight: 460 }} bodyStyle={{ padding: activeWorkers.length ? '0 8px' : 0 }}>
              {activeWorkers.length === 0 ? (
                <EmptyBox text="No one is clocked in" />
              ) : (
                <List
                  dataSource={activeWorkers}
                  renderItem={(worker) => {
                    const statusMeta = TIME_ENTRY_STATUS[worker.status] || {};
                    const onBreak = worker.status === 'on_break';
                    return (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            <Avatar style={{ background: OPS_COLORS.brand }}>
                              {initials(worker.name)}
                            </Avatar>
                          }
                          title={<span style={{ fontWeight: 600 }}>{worker.name}</span>}
                          description={
                            <div style={{ color: OPS_COLORS.grey, fontSize: 13 }}>
                              <div>
                                <EnvironmentOutlined /> {worker.location || '—'}
                                {worker.work_type ? ` · ${worker.work_type}` : ''}
                              </div>
                              {worker.clock_in_at ? (
                                <div>
                                  <ClockCircleOutlined /> Clocked in at{' '}
                                  {commonHelper.displayDateTimeLocal(worker.clock_in_at, 'h:mm A')}
                                </div>
                              ) : null}
                            </div>
                          }
                        />
                        <div style={{ textAlign: 'right', minWidth: 110 }}>
                          <div
                            style={{
                              fontSize: 20,
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                              color: onBreak ? OPS_COLORS.orange : OPS_COLORS.greenBright,
                            }}
                          >
                            {formatDuration(liveSeconds(worker))}
                          </div>
                          <Tag color={statusMeta.color} style={{ marginTop: 4, marginRight: 0 }}>
                            {statusMeta.label}
                          </Tag>
                          {canManageOthers || worker.user_id === currentUser?.id ? (
                            <div
                              style={{
                                marginTop: 8,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 8,
                              }}
                            >
                              <Button
                                size="small"
                                onClick={() =>
                                  runAction(
                                    onBreak
                                      ? timeTrackerService.endBreak(worker.id)
                                      : timeTrackerService.startBreak(worker.id),
                                    onBreak ? 'Break ended' : 'Break started'
                                  )
                                }
                              >
                                {onBreak ? 'End break' : 'Break'}
                              </Button>
                              <Button
                                size="small"
                                danger
                                onClick={() =>
                                  runAction(timeTrackerService.clockOut(worker.id), 'Clocked out')
                                }
                              >
                                Clock out
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              )}
            </SectionCard>
          </Col>

          <Col xs={24} lg={10}>
            <SectionCard title="Recent Activity" fill style={{ minHeight: 460 }} bodyStyle={{ padding: recent.length ? '0 8px' : 0 }}>
              {recent.length === 0 ? (
                <EmptyBox text="No activity today" />
              ) : (
                <List
                  dataSource={recent}
                  renderItem={(event) => {
                    const meta = recentIcon(event.label);
                    return (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            <Avatar size="small" style={{ background: '#f5f5f5', color: meta.color }} icon={meta.icon} />
                          }
                          title={<span style={{ fontWeight: 500, fontSize: 13 }}>{event.label}</span>}
                          description={
                            event.time ? commonHelper.displayDateTimeLocal(event.time, 'h:mm A') : null
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              )}
            </SectionCard>
          </Col>
        </Row>

        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>By Property</h2>
          <Row gutter={[16, 16]}>
            {byLocation.map((loc) => (
              <Col xs={24} sm={12} lg={6} key={loc.location}>
                <StatCard
                  label={loc.location}
                  value={loc.count}
                  sub="Active workers"
                  icon={<EnvironmentOutlined />}
                />
              </Col>
            ))}
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Total Hours Today"
                value={dashboard?.hours_today || 0}
                sub="Across all properties"
                icon={<ClockCircleOutlined />}
              />
            </Col>
          </Row>
        </div>
      </Spin>

      <Modal
        title="Clock In"
        visible={clockInOpen}
        onCancel={() => setClockInOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText="Clock In"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ location: activeProperty?.name, worker: currentUser?.id }}
          onFinish={handleClockIn}
        >
          {canManageOthers ? (
            <Form.Item
              name="worker"
              label="Worker"
              rules={[{ required: true, message: 'Select a worker to clock in' }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="Select a worker"
              >
                {workerOptions.map((w) => (
                  <Option key={w.user_id} value={w.user_id}>
                    {w.name}
                    {w.user_id === currentUser?.id ? ' (me)' : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <div style={{ marginBottom: 16, color: OPS_COLORS.grey }}>
              Clocking in as <strong>{currentUser?.name}</strong>
            </div>
          )}
          <Form.Item name="work_type" label="Work type">
            <Select placeholder="Select a work type" allowClear>
              {WORK_TYPES.map((t) => (
                <Option key={t} value={t}>
                  {t}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="e.g. Springfield Station" />
          </Form.Item>
        </Form>
      </Modal>
    </LayoutWrapper>
  );
};

export default TimeTracker;
